import React, { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import PublicRouter from "./PublicRouter";
import PrivateRouter from "./PrivateRouter";
import { DEFAULT_APP_VERSION } from "../constants/login";
import { isMultiVariant } from "../config/useRouterVersionConfig";

type Props = {
  variant: string;
};

function normalizeVariant(v: string) {
  return (v || DEFAULT_APP_VERSION).replace(/^\/+/, "").toLowerCase();
}

function getPrefixVariantFromPathname(pathname: string): string | null {
  const first = pathname.split("/")[1];
  if (/^v\d+$/i.test(first)) return first.toLowerCase();
  return null;
}

function stripLeadingVariantPrefix(pathname: string): string {
  return pathname.replace(/^\/v\d+/i, "") || "/";
}

function isPublicPathname(pathname: string): boolean {
  const p = pathname.toLowerCase();

  return (
    p === "/login" ||
    p.startsWith("/login/") ||
    p === "/login-offline" ||
    p.startsWith("/login-offline/") ||
    p === "/terms" ||
    p.startsWith("/terms/") ||
    p === "/dual-compat-demo" ||
    p === "" ||
    p === "/"
  );
}

export default function RootRouter({ variant }: Props) {
  const v = useMemo(() => normalizeVariant(variant) || DEFAULT_APP_VERSION, [variant]);
  // A "vx" account has no single version of its own — it picks one at login
  // (VariantSelector), and the chosen /v{N}/ prefix is then already in the
  // URL. "vx" must never be used AS a prefix: getPrefixVariantFromPathname
  // only matches /^v\d+$/, so redirecting to "/vx/..." would leave urlPrefix
  // null on the next pass and redirect forever. Send such a request back to
  // /login to choose instead.
  const isVx = useMemo(() => isMultiVariant(variant), [variant]);

  const pathname = window.location.pathname;
  const urlPrefix = getPrefixVariantFromPathname(pathname);
  const pathWithoutPrefix = urlPrefix ? stripLeadingVariantPrefix(pathname) : pathname;

  const isPublic = isPublicPathname(pathWithoutPrefix);

  // Accept any /v{N}/ prefix already present in the URL; only add a prefix when missing.
  // This prevents hard redirects when navigating to a version-prefixed URL that differs
  // from REACT_APP_VERSION (e.g. /v3/monitoring when app is built with REACT_APP_VERSION=v1).
  const effectivePrefix = urlPrefix ?? v;

  useEffect(() => {
    if (isPublic && urlPrefix) {
      const newPath = pathWithoutPrefix;
      window.location.replace(`${newPath}${window.location.search}${window.location.hash}`);
      return;
    }

    if (!isPublic && !urlPrefix) {
      const newPath = isVx
        ? "/login"
        : `/${effectivePrefix}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
      window.location.replace(`${newPath}${window.location.search}${window.location.hash}`);
    }
  }, [isPublic, isVx, urlPrefix, effectivePrefix, pathname, pathWithoutPrefix]);

  const isRedirectNeeded = (isPublic && !!urlPrefix) || (!isPublic && !urlPrefix);

  if (isRedirectNeeded) return null;

  if (isPublic) {
    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PublicRouter />
      </BrowserRouter>
    );
  }

  if (
    process.env.REACT_APP_VERSION === "v2" ||
    process.env.REACT_APP_VERSION === "v1" ||
    process.env.REACT_APP_VERSION === "v3" ||
    process.env.REACT_APP_ENVIRONMENT === "OFFLINE"
  ) {
    return (
      <BrowserRouter
        basename={`/${effectivePrefix}`}
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <PrivateRouter />
      </BrowserRouter>
    );
  }
  // }else 	if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
  // 	return (
  // 		<BrowserRouter >
  // 			<PrivateRouter />
  // 		</BrowserRouter>
  // 	);
  // }

  return null;
}
